/* eslint-disable jsx-a11y/no-autofocus */
import { css, styled } from 'styled-components';
import { Ref } from 'react';

import { theme } from '../../../styles';
import { getColor } from '../../../utils';
import { Container } from '../../Layout';
import { ErrorMessage } from '../ErrorMessage';
import { Label } from '../Label';
import { ClearIcon, MenuCloseIcon } from '../../../assets/icons';
import { NoStyleButton } from '../../NoStyleButton';

const InputWrapper = styled.div<{ disabled?: boolean; isFocused: boolean; hasError: boolean; isSearch?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  background-color: inherit;
  width: 100%;
`;

export const StyledInput = styled.input<{
  disabled?: boolean;
  hasIcon?: boolean;
  hasPreambleIcon?: boolean;
  hasBorder: boolean;
  hasError: boolean;
  textAlign?: 'left' | 'right' | 'center' | undefined;
  isSearch?: boolean;
  isActive?: boolean;
  isDatePicker?: boolean;
}>`
  ${theme.typography.textStandard};
  box-sizing: border-box;
  padding: 0rem ${props => (props.hasIcon ? 4.125 : 1.25)}rem 0rem ${props => (props.hasPreambleIcon ? 2.75 : 1.25)}rem;
  width: 100%;
  height: 3.75rem;

  background-color: ${theme.colors.white};
  border-radius: ${props => (props.isSearch ? '50px' : '2px')};
  border: solid ${props => (props.hasBorder ? '1px' : '0px')} ${theme.colors.black60};
  appearance: none;
  outline: none;
  text-align: ${props => props.textAlign};

  ${props =>
    props.isActive &&
    !props.isDatePicker &&
    css`
      border: 2px solid ${theme.colors.darkBlue};
    `}

  &:focus {
    border: 2px solid ${theme.colors.darkBlue};
  }

  ${props =>
    props.disabled &&
    css`
      background-color: ${theme.colors.black20};
      color: ${theme.colors.black30};
      :hover {
        cursor: not-allowed;
      }
    `}
  ${props =>
    props.hasError &&
    css`
      background-color: ${theme.colors.babyPink};
      border: 2px solid ${theme.colors.burgundy};
    `}
  @media screen and (min-width: ${theme.breakpoint.lg}px) {
    height: 3.125rem;
  }
`;

const PreambleIconWrapper = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  background-color: transparent;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 0.875rem;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  background-color: transparent;
  @media screen and (min-width: ${theme.breakpoint.lg}px) {
    right: 0.75rem;
  }
`;

export type InputFieldProps = {
  /** The `<InputField/>` id to identify the field and to connect a `<Label/>` to a specific `<InputField/>`,
   * where as clicking on the `<Label/>` will trigger onClick event on the input, useful when using i.e. <RadioButton/> or >CheckBox/> */
  id: string;
  /** Type of field the input should be, default is 'text' but there are others such as 'email', 'password', 'phone' etc.. */
  type?: string;
  value: string;
  /** Text to display as a placeholder */
  label: string;
  name?: string;
  labelColor?: 'black70' | 'white' | string;
  errors: string[];
  /** Icon to display */
  preambleIcon?: React.ReactNode;
  icon?: React.ReactNode;
  /** Adds or removes outer border */
  withBorder?: boolean;
  /** Sets autocomplete for the field */
  autocomplete?: 'on' | 'off' | 'none' | 'tel' | 'email' | 'disabled';
  /** Sets the Disabled state */
  disabled?: boolean;
  /** Change variant of error, either red text, or white text with error icon */
  errorVariant?: 'light' | 'dark';
  /** Sets the Active/Focus state */
  isActive: boolean;
  /** Toggles if the field should be automatically focused after rendering */
  autoFocus?: boolean;
  placeholder?: string;
  hideLabel?: boolean;
  textAlign?: 'left' | 'right' | 'center' | undefined;
  /** hints at the type of data that might be entered. This allows a browser to display an appropriate virtual keyboard. */
  inputMode?: 'text' | 'none' | 'tel' | 'email' | 'search' | 'url' | 'numeric' | 'decimal' | undefined;
  /** ref for <input> */
  innerRef?: Ref<HTMLInputElement>;
  /** Clears the input value and renders a clear button */
  clearInput?: () => void;
  /** Used for different styling */
  isSearch?: boolean;
  /** Aria-label */
  ariaLabel?: string;
  hideLabelOnFocus?: boolean;
  hideInputError?: boolean;
  /** Used in datepicker for styling of inputfield */
  isDatePicker?: boolean;
} & Omit<React.ComponentProps<typeof StyledInput>, 'hasError' | 'hasBorder'>;

export const InputField = ({
  autoFocus = false,
  id,
  name,
  type = 'text',
  value,
  label,
  labelColor = 'black70',
  isActive,
  errors,
  preambleIcon,
  icon,
  withBorder = true,
  autocomplete = 'off',
  disabled,
  errorVariant = 'light',
  onChange,
  onFocus,
  onBlur,
  placeholder,
  hideLabel = false,
  textAlign = 'left',
  inputMode,
  innerRef,
  clearInput,
  isSearch = false,
  hideLabelOnFocus = false,
  ariaLabel,
  hideInputError = false,
  isDatePicker = false,
  ...inputProps
}: InputFieldProps) => {
  const hasError = errors.length > 0;
  return (
    <Container width="100%" style={{ backgroundColor: 'inherit' }}>
      {!hideLabel && (
        <Label
          errorVariant={errorVariant}
          hasError={hasError}
          hasPreambleIcon={!!preambleIcon}
          id={id}
          labelColor={getColor(labelColor)}
          hideOnFocus={hideLabelOnFocus || isSearch}
        >
          {label}
        </Label>
      )}
      <InputWrapper isFocused={isActive} hasError={hasError} disabled={disabled} isSearch={isSearch}>
        {preambleIcon && <PreambleIconWrapper>{preambleIcon}</PreambleIconWrapper>}

        <StyledInput
          {...inputProps}
          autoFocus={autoFocus}
          id={id}
          name={name}
          data-testid={`test-${id}`}
          type={type}
          value={value}
          hasIcon={!!icon}
          hasPreambleIcon={!!preambleIcon}
          hasError={hasError}
          disabled={disabled}
          hasBorder={withBorder}
          autoComplete={autocomplete}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          title={label}
          textAlign={textAlign}
          inputMode={inputMode}
          ref={innerRef}
          isSearch={isSearch}
          aria-label={ariaLabel}
          isActive={(!isDatePicker && isActive) || (!isDatePicker && !!value)}
          isDatePicker={isDatePicker}
        />
        {(icon || clearInput) && (
          <IconWrapper>
            {clearInput && value ? (
              <NoStyleButton onClick={clearInput}>
                {isSearch ? (
                  <MenuCloseIcon containerSize={13} color="black90" marginRight={14} />
                ) : (
                  <ClearIcon containerSize={30} />
                )}
              </NoStyleButton>
            ) : (
              icon
            )}
          </IconWrapper>
        )}
      </InputWrapper>
      {hasError &&
        !hideInputError &&
        errors.map((error, index) => (
          <ErrorMessage key={index} testId={`test-${id}-error`} variant={errorVariant}>
            {error}
          </ErrorMessage>
        ))}
    </Container>
  );
};
